import React, { useState, useEffect } from 'react';
import ChatBox from './components/ChatBox';
import WorkflowVisualizer from './components/WorkflowVisualizer';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import agentWorkflowService from './api'; 

const RawDataView = ({ graphData }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!graphData) return null;

    return (
        <div className="mt-6 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-semibold text-gray-800">Raw Workflow Data</h2>
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-sm text-primary hover:text-primaryDark"
                >
                    {isExpanded ? 'Hide' : 'Show'} Raw Data
                </button>
            </div>

            {isExpanded && (
                <pre className="bg-gray-50 p-4 rounded-md overflow-auto text-xs text-gray-700 max-h-[300px] max-w-[100%]">
                    {JSON.stringify(graphData, null, 2)}
                </pre>
            )}
        </div>
    );
};

const AgentWorkflow = () => {
    const [graphData, setGraphData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFullGraph, setIsFullGraph] = useState(false);
    const [useNovaV2, setUseNovaV2] = useState(false);
    const navigate = useNavigate();
    
    // Reset scroll position when component mounts
    

    const handleWorkflowUpdate = (newGraphData) => {
        setGraphData(newGraphData);
        setIsFullGraph(false);
    };

    const handleFetchFullGraph = async () => {
        setLoading(true);
        setError(null);
        try {
            const userId = sessionStorage.getItem('user_id') || undefined;
            const fullGraphData = useNovaV2
                ? await agentWorkflowService.getFullAgentGraphV2(userId)
                : await agentWorkflowService.getFullAgentGraph();
            setGraphData(fullGraphData);
            setIsFullGraph(true);
        } catch (err) {
            setError(`Failed to load full graph: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6">
            

            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                <div className="lg:col-span-1">
                    <div className="mb-4">
                        <ChatBox
                            onWorkflowUpdate={handleWorkflowUpdate}
                            onQueryStart={() => setGraphData(null)}
                            useNovaV2={useNovaV2}
                            onToggleV2={() => setUseNovaV2(v => !v)}
                        />
                    </div>
                </div>

                <div className="lg:col-span-1">
                    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-primary">
                                {isFullGraph ? "Full Agent Workflow" : "Agent Workflow"}
                            </h2>
                            <button
                                onClick={handleFetchFullGraph}
                                disabled={loading || isFullGraph}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                                    ${isFullGraph 
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                                        : 'bg-primary text-white hover:bg-primaryDark'}`}
                            >
                                {loading ? 'Loading...' : isFullGraph ? 'Full Graph Loaded' : 'See Full Graph'}
                            </button>
                        </div>
                        <WorkflowVisualizer
                            graphData={graphData}
                            loading={loading}
                            error={error}
                            useNovaV2={useNovaV2}
                        />
                    </div>
                </div>
            </div>
            {graphData && <RawDataView graphData={graphData} />}
        </div>
    );
};

export default AgentWorkflow;